import React, { useState } from 'react';
import { observer } from 'mobx-react';

import EditIcon from '@mui/icons-material/Edit';

import { ScheduleInstanceResponse, ScheduleStatuses } from 'utils/api/modules/schedules/schedules.types';
import { ScheduleStatus, TaskStatus } from 'utils/api/modules/tasks/tasks.constants';
import { LinksType, UuidableName } from 'utils/api/types';
import { formatMoment } from 'utils/formatters/timezone';

import { Button } from 'components/Button';
import InitialAvatar from 'components/Avatar/InitialAvatar';
import EntityDisplayValue from 'components/Entities/EntityDisplayValue/EntityDisplayValue';
import { useEntityDisplayValue } from 'components/Entities/EntityDisplayValue/utils';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField/CustomUserDropdownField';

import TaskStatusChip from 'containers/TaskDetails/components/TaskStatusChip';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, UserRole } from 'core/constants';

import { useStore } from 'utils/hooks/store';
import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import {
  ContentContainer,
  FieldContainer,
  FieldLabel,
  FieldValue,
  DueByValue,
  AssigneeList,
  AssigneeRow,
  EditableAssigneeRow,
  AssigneeEditActions,
} from './styles';
import { SupportedEntityModule } from './constants';
import { Tooltip } from '@mui/material';

interface EntityFieldProps {
  module: SupportedEntityModule;
  links: LinksType[];
}

const EntityField: React.FC<EntityFieldProps> = ({ module, links }) => {
  const { path, entityLink, singular } = useEntityDisplayValue(module, links);
  return (
    <FieldContainer>
      <FieldLabel>{singular}</FieldLabel>
      {entityLink ? <EntityDisplayValue link={entityLink.instance.name} path={path} /> : '-'}
    </FieldContainer>
  );
};

interface DrawerContentProps {
  data: ScheduleInstanceResponse;
  links: LinksType[];
  filteredEntityModules: SupportedEntityModule[];
  assigneeLoading?: boolean;
  onAssigneeChange?: (user: { uuid: string; name: string }) => void;
}

const ACTIONED_STATUSES = new Set([ScheduleStatuses.IN_PROGRESS, ScheduleStatuses.COMPLETED, ScheduleStatuses.WONT_DO]);

const DrawerContent: React.FC<DrawerContentProps> = ({
  data,
  links,
  filteredEntityModules,
  assigneeLoading,
  onAssigneeChange,
}) => {
  const { session } = useStore();

  const isActioned = !!data && ACTIONED_STATUSES.has(data.status);
  const hasReassignRole = ADMIN_AND_ACCOUNT_MANAGERS.includes(session?.user?.role as UserRole);
  const canEditAssignee = hasReassignRole && !!data && !isActioned;

  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [pendingAssignee, setPendingAssignee] = useState<UuidableName | null>(null);

  const handleCancel = (): void => {
    setIsEditingAssignee(false);
    setPendingAssignee(null);
  };

  const handleSave = (): void => {
    if (pendingAssignee && pendingAssignee.uuid !== data.assignedTo.uuid) {
      onAssigneeChange?.({
        uuid: pendingAssignee.uuid,
        name: pendingAssignee.name || '',
      });
    }
  };

  return (
    <ContentContainer>
      <FieldContainer>
        <FieldLabel>Status</FieldLabel>
        <Tooltip
          title={
            data.status === ScheduleStatuses.UPCOMING
              ? 'This audit cannot be started yet. It will become available on its scheduled start date.'
              : ''
          }
          arrow
        >
          <span>
            <TaskStatusChip status={data.status as unknown as TaskStatus | ScheduleStatus} />
          </span>
        </Tooltip>
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Template</FieldLabel>
        <FieldValue>{data.template?.name}</FieldValue>
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Available From</FieldLabel>
        <FieldValue data-testid="schedule-run-available-from">
          {formatMoment(data.startDate, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
        </FieldValue>
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Due By</FieldLabel>
        <DueByValue $overdue={data.status === ScheduleStatuses.OVERDUE} data-testid="schedule-run-due-by">
          {formatMoment(data.dueDate, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
        </DueByValue>
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Assigned To</FieldLabel>
        {isEditingAssignee ? (
          <>
            <CustomUserDropdownField
              value={data.assignedTo}
              onChange={setPendingAssignee}
              description="Assigned To"
              licenses="workflows"
            />
            <AssigneeEditActions>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={assigneeLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={assigneeLoading || !pendingAssignee || pendingAssignee.uuid === data.assignedTo.uuid}
              >
                {assigneeLoading ? 'Saving...' : 'Save'}
              </Button>
            </AssigneeEditActions>
          </>
        ) : (
          <AssigneeList>
            {canEditAssignee ? (
              <EditableAssigneeRow onClick={() => setIsEditingAssignee(true)}>
                <InitialAvatar name={data.assignedTo.name || 'N/A'} size="small" />
                <FieldValue>{data.assignedTo.name}</FieldValue>
                <EditIcon />
              </EditableAssigneeRow>
            ) : (
              <Tooltip
                title={
                  hasReassignRole && isActioned
                    ? "This audit can't be reassigned because it has already been started, completed, or marked as won't do."
                    : ''
                }
                arrow
              >
                <AssigneeRow>
                  <InitialAvatar name={data.assignedTo.name || 'N/A'} size="small" />
                  <FieldValue>{data.assignedTo.name}</FieldValue>
                </AssigneeRow>
              </Tooltip>
            )}
          </AssigneeList>
        )}
      </FieldContainer>

      {filteredEntityModules.map((module) => (
        <EntityField key={module} module={module} links={links} />
      ))}
    </ContentContainer>
  );
};

export default observer(DrawerContent);
